import { MonthString, Properties } from "@util/types";
import _ from "lodash";
import z from "zod";

export enum PerModuleFeeType {
	PayPerUse = "pay-per-use",
	PayOnDemand = "pay-on-demand",
	FlatFee = "flatFee",
	FlatPerPack = "flat-per-pack",
	FlatPerUse = "flat-per-use",
	FlatPerLoggedIn = "flat-per-logged-in",
}

export type PricingSettings = PricingRecord[];

export default class PricingRecord {
	constructor(props: Omit<Properties<PricingRecord>, "id">) {
		this.availableFrom = props.availableFrom;
		this.availableUntil = props.availableUntil;
		this.yearlyBilling = props.yearlyBilling;
		this.minimumFee = props.minimumFee;
		this.flatFee = props.flatFee;
		this.flatFeePerEmployee = props.flatFeePerEmployee;
		this.flatFeePerEmployeePack = props.flatFeePerEmployeePack;
		this.feePerModuleAndEmployee = props.feePerModuleAndEmployee;
		this.id = _.uniqueId("pricingRecord_");
	}

	readonly availableFrom: Date | null;
	readonly availableUntil: Date | null;
	readonly id?: string;

	readonly yearlyBilling?: null | {
		/** 1-12 */
		readonly month: number;
		readonly resetUsageMonth: MonthString | null;
	};

	readonly minimumFee: number;

	readonly flatFee: null | {
		amount: number;
		description: string | null;
	};

	readonly flatFeePerEmployee: null | {
		amount: number;
		description: string | null;
	};

	readonly flatFeePerEmployeePack?: null | {
		amount: number;
		minNumberOfPacks: number;
		description: string | null;
	};

	readonly feePerModuleAndEmployee: null | {
		[id: string]: FeePerModuleAndEmployee;
	};

	public static fromJson(
		data: z.infer<typeof pricingRecordSchema>
	): PricingRecord {
		return new PricingRecord(data);
	}
}

export type FeePerModuleAndEmployee = {
	type: PerModuleFeeType | null;
	amount: number | null;
	minNumberOfPacks?: number;
};

const flatFeeSchema = z
	.object({
		amount: z.number(),
		description: z.string().nullable(),
	})
	.nullable()
	.default(null);

const flatFeePerEmployeeSchema = z
	.object({
		amount: z.number(),
		description: z.string().nullable(),
	})
	.nullable()
	.default(null);

const flatFeePerEmployeePackSchema = z
	.object({
		amount: z.number(),
		description: z.string().nullable(),
		minNumberOfPacks: z.int().min(0),
	})
	.nullable()
	.default(null);

const feePerModuleAndEmployeeSchema = z
	.record(
		z.string(),
		z.object({
			type: z.enum(Object.values(PerModuleFeeType)),
			amount: z.number(),
			minNumberOfPacks: z.int().min(0).optional(),
		})
	)
	.nullable()
	.default(null);

const monthStringSchema = z.custom<MonthString>(
	(s: unknown): s is MonthString =>
		typeof s === "string" && z.iso.date().safeParse(`${s}-01`).success,
	{
		error: "Month must be in the format YYYY-MM, e.g., 2024-01",
	}
);

const yearlyBillingSchema = z
	.object({
		month: z.int().min(1).max(12),
		resetUsageMonth: monthStringSchema.nullable(),
	})
	.nullable()
	.default(null);

const pricingRecordSchema = z.object({
	availableFrom: z.coerce.date().nullable(),
	availableUntil: z.coerce.date().nullable(),
	yearlyBilling: yearlyBillingSchema,
	minimumFee: z.number(),
	flatFee: flatFeeSchema,
	flatFeePerEmployee: flatFeePerEmployeeSchema,
	flatFeePerEmployeePack: flatFeePerEmployeePackSchema,
	feePerModuleAndEmployee: feePerModuleAndEmployeeSchema,
});

export const pricingSettingsSchema = z
	.array(pricingRecordSchema)
	.nullable()
	.default(null);
